import React, { useState, useMemo } from 'react';
import { Plane, Calendar, Users, X, ArrowRightLeft, Plus, Minus } from 'lucide-react';
import popularAirportsData from '../data/popular_airports.json';
import BottomSheet from './BottomSheet';

// Lazy load full airports
let fullAirportsData = null;
const loadFullAirports = async () => {
  if (!fullAirportsData) {
    const module = await import('../data/airports.json');
    fullAirportsData = module.default;
  }
  return fullAirportsData;
};

const MobileModifySearch = ({ 
  isOpen, 
  onClose, 
  tripType: initialTripType = 'oneWay',
  fromAirport, 
  setFromAirport,
  toAirport, 
  setToAirport,
  departureDate, 
  setDepartureDate,
  returnDate,
  setReturnDate,
  adults, 
  setAdults,
  children, 
  setChildren,
  infants, 
  setInfants,
  travelClass, 
  setTravelClass,
  onSearch
}) => {
  const [activeField, setActiveField] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tripType, setTripType] = useState(initialTripType);
  const [allAirports, setAllAirports] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Use popular airports by default
  const airports = useMemo(() => {
    return allAirports || popularAirportsData;
  }, [allAirports]);

  // Lazy load when searching
  const handleSearchChange = async (value) => {
    setSearchQuery(value);
    
    if (value.length > 0 && !allAirports) {
      setIsSearching(true);
      try {
        const fullData = await loadFullAirports();
        setAllAirports(fullData);
      } catch (error) {
        console.error('Failed to load airports:', error);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleSwap = () => {
    const temp = fromAirport;
    setFromAirport(toAirport);
    setToAirport(temp);
  };

  const handleSearch = () => {
    onSearch();
    onClose();
  };

  const filteredAirports = useMemo(() => {
    if (!searchQuery) {
      return popularAirportsData.slice(0, 50);
    }
    
    const results = airports.filter(airport =>
      airport.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      airport.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return results.slice(0, 100);
  }, [searchQuery, airports]);

  const totalPassengers = adults + children + infants;

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Modify Search">
        <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3 pb-6">
          {/* Trip Type Selector - Compact */}
          <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg">
            {[
              { id: 'oneWay', label: 'One-Way' },
              { id: 'roundTrip', label: 'Round Trip' }
            ].map((type) => (
              <button
                key={type.id}
                onClick={() => setTripType(type.id)}
                className={`flex-1 py-2 px-1.5 sm:px-2 rounded-md text-[11px] sm:text-xs font-bold transition-all ${
                  tripType === type.id
                    ? 'bg-white text-[#1B375B] shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {tripType !== 'multiCity' && (
            <>
              {/* From Airport - Compact */}
              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">From</label>
                <button
                  onClick={() => setActiveField('from')}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-left flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="font-bold text-sm text-[#1B375B]">{fromAirport}</p>
                      <p className="text-[10px] text-gray-500">
                        {airports.find(a => a.code === fromAirport)?.city || 'Select'}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Swap Button - Compact */}
              <div className="flex justify-center -my-1.5">
                <button
                  onClick={handleSwap}
                  className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white shadow-md active:scale-95 transition-transform"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </button>
              </div>

              {/* To Airport - Compact */}
              <div>
                <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">To</label>
                <button
                  onClick={() => setActiveField('to')}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-left flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="font-bold text-sm text-[#1B375B]">{toAirport}</p>
                      <p className="text-[10px] text-gray-500">
                        {airports.find(a => a.code === toAirport)?.city || 'Select'}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Dates - Compact with proper styling */}
              <div className={`grid ${tripType === 'roundTrip' ? 'grid-cols-2' : 'grid-cols-1'} gap-1.5 sm:gap-2`}>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Departure</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={departureDate}
                      onChange={(e) => setDepartureDate(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-xs font-semibold text-[#1B375B] appearance-none"
                      style={{ 
                        colorScheme: 'light',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none'
                      }}
                    />
                  </div>
                </div>
                {tripType === 'roundTrip' && (
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Return</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-xs font-semibold text-[#1B375B] appearance-none"
                        style={{ 
                          colorScheme: 'light',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Passengers & Class - Compact Combined */}
              <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Travellers</label>
                  <button
                    onClick={() => setActiveField('passengers')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <p className="font-bold text-sm text-[#1B375B]">{totalPassengers}</p>
                    </div>
                  </button>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Class</label>
                  <button
                    onClick={() => setActiveField('class')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-left"
                  >
                    <p className="font-bold text-sm text-[#1B375B] truncate">{travelClass}</p>
                  </button>
                </div>
              </div>

              {/* Search Button - Compact */}
              <button
                onClick={handleSearch}
                className="w-full bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white font-bold py-3 rounded-lg shadow-lg active:scale-98 transition-transform mt-2 text-sm sm:text-base"
              >
                Search Flights
              </button>
            </>
          )}
        </div>
      </BottomSheet>

      {/* Airport Selector Bottom Sheet */}
      {(activeField === 'from' || activeField === 'to') && (
        <div className="fixed inset-0 bg-black/50 z-[10000]" onClick={() => setActiveField(null)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-[#1B375B]">
                  Select {activeField === 'from' ? 'From' : 'To'} Airport
                </h3>
                <button onClick={() => setActiveField(null)} className="p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <input
                type="text"
                placeholder="Search city or airport"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-100 rounded-lg text-sm"
                autoFocus
              />
              {isSearching && (
                <div className="absolute right-6 top-16">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#659A9B]"></div>
                </div>
              )}
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-4">
              {filteredAirports.map((airport) => (
                <button
                  key={airport.code}
                  onClick={() => {
                    if (activeField === 'from') setFromAirport(airport.code);
                    else setToAirport(airport.code);
                    setActiveField(null);
                    setSearchQuery('');
                  }}
                  className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 rounded-lg border-b border-gray-100 last:border-0"
                >
                  <p className="font-bold text-[#1B375B]">{airport.city}</p>
                  <p className="text-sm text-gray-500">{airport.code} - {airport.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Passenger Selector Bottom Sheet */}
      {activeField === 'passengers' && (
        <div className="fixed inset-0 bg-black/50 z-[10000]" onClick={() => setActiveField(null)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#1B375B]">Travellers</h3>
              <button onClick={() => setActiveField(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Adults */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-semibold text-[#1B375B]">Adults</p>
                  <p className="text-xs text-gray-500">12+ years</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-lg w-6 text-center">{adults}</span>
                  <button
                    onClick={() => setAdults(Math.min(9, adults + 1))}
                    className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center active:bg-teal-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-semibold text-[#1B375B]">Children</p>
                  <p className="text-xs text-gray-500">2-12 years</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setChildren(Math.max(0, children - 1))}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-lg w-6 text-center">{children}</span>
                  <button
                    onClick={() => setChildren(Math.min(9, children + 1))}
                    className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center active:bg-teal-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Infants */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-semibold text-[#1B375B]">Infants</p>
                  <p className="text-xs text-gray-500">Below 2 years</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setInfants(Math.max(0, infants - 1))}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-lg w-6 text-center">{infants}</span>
                  <button
                    onClick={() => setInfants(Math.min(adults, infants + 1))}
                    className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center active:bg-teal-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => setActiveField(null)}
              className="w-full mt-6 bg-teal-600 text-white font-bold py-3 rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Class Selector Bottom Sheet */}
      {activeField === 'class' && (
        <div className="fixed inset-0 bg-black/50 z-[10000]" onClick={() => setActiveField(null)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#1B375B]">Travel Class</h3>
              <button onClick={() => setActiveField(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              {['Economy', 'Premium Economy', 'Business', 'First'].map((classType) => (
                <button
                  key={classType}
                  onClick={() => {
                    setTravelClass(classType);
                    setActiveField(null);
                  }}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    travelClass === classType
                      ? 'border-teal-600 bg-teal-50'
                      : 'border-gray-200 bg-white active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-[#1B375B]">{classType}</p>
                    {travelClass === classType && (
                      <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileModifySearch;
